import React, { useEffect, useMemo, useRef, useState } from 'react';
import Hls from 'hls.js';
import { arrayOf, bool, func, number, shape } from 'prop-types';
import {
  ModalBody,
  ModalBackdrop,
  ModalClose,
  ModalContent,
  ModalDescription,
  ModalHeaderControls,
  ModalNavButton,
  ModalPlayButton,
  ModalProgress,
  ModalProgressBar,
  ModalProgressLabel,
  ModalHeader,
  ModalTitle,
  ModalToggleButton,
  ModalVideo,
} from './PlayerModal.styles';

const IMAGE_AUTOPLAY_MS = 5000;

function PlayerModal({
  isOpen,
  onClose,
  items,
  initialIndex,
}) {
  const [index, setIndex] = useState(initialIndex);
  const [autoplay, setAutoplay] = useState(true);
  const [imageProgress, setImageProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoAutoplayEnabled, setVideoAutoplayEnabled] = useState(false);
  const [activeVideoSrc, setActiveVideoSrc] = useState('');
  const videoRef = useRef(null);
  const closeButtonRef = useRef(null);
  const overlayPlayButtonRef = useRef(null);
  const hlsRef = useRef(null);
  const timerRef = useRef(null);

  const total = items.length;
  const media = items[index];

  useEffect(() => {
    if (isOpen) {
      setIndex(initialIndex);
      setVideoAutoplayEnabled(false);
    }
  }, [initialIndex, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKey = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') setIndex((prev) => (prev - 1 + total) % total);
      if (event.key === 'ArrowRight') setIndex((prev) => (prev + 1) % total);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose, total]);

  useEffect(() => {
    if (!isOpen || !autoplay || !media || media.videoPath) return undefined;
    const start = performance.now();
    setImageProgress(0);
    timerRef.current = requestAnimationFrame(function tick(now) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / IMAGE_AUTOPLAY_MS, 1);
      setImageProgress(progress);
      if (progress >= 1) {
        setIndex((prev) => (prev + 1) % total);
        return;
      }
      timerRef.current = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(timerRef.current);
  }, [autoplay, isOpen, media, total]);

  useEffect(() => {
    if (!media?.videoPath || !autoplay) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;
    const handleEnded = () => setIndex((prev) => (prev + 1) % total);
    video.addEventListener('ended', handleEnded);
    return () => video.removeEventListener('ended', handleEnded);
  }, [autoplay, media, total]);

  useEffect(() => {
    if (!isOpen) {
      setVideoAutoplayEnabled(false);
      setIsPlaying(false);
      setActiveVideoSrc('');
      return;
    }
    if (!media?.videoPath) {
      setIsPlaying(false);
      setActiveVideoSrc('');
      return;
    }
    const canNativeHls = document
      .createElement('video')
      .canPlayType('application/vnd.apple.mpegurl');
    const prefersMp4Fallback = Boolean(
      media.isMux
      && media.videoPath.includes('.m3u8')
      && media.muxMp4FallbackPath
      && !canNativeHls,
    );
    setActiveVideoSrc(prefersMp4Fallback ? media.muxMp4FallbackPath : media.videoPath);

    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
    setIsPlaying(videoAutoplayEnabled);
  }, [isOpen, media, videoAutoplayEnabled]);

  useEffect(() => {
    const video = videoRef.current;
    const source = activeVideoSrc || media?.videoPath;
    if (!video || !source) return undefined;

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const canNativeHls = Boolean(
      video.canPlayType('application/vnd.apple.mpegurl'),
    );
    const isHlsSource = source.includes('.m3u8');

    const playIfRequested = () => {
      if (!isPlaying) return;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    };

    if (isHlsSource && !canNativeHls && Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
      });
      hlsRef.current = hls;
      hls.loadSource(source);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, playIfRequested);
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (
          data?.fatal
          && media?.isMux
          && media?.muxMp4FallbackPath
          && source !== media.muxMp4FallbackPath
        ) {
          setActiveVideoSrc(media.muxMp4FallbackPath);
        }
      });
    } else {
      video.src = source;
      video.load();
      playIfRequested();
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [
    activeVideoSrc,
    isPlaying,
    media?.isMux,
    media?.muxMp4FallbackPath,
    media?.videoPath,
  ]);

  const showPlayOverlay = useMemo(
    () => media?.videoPath && !isPlaying,
    [isPlaying, media],
  );

  useEffect(() => {
    if (!isOpen) return;
    const target = showPlayOverlay ? overlayPlayButtonRef.current : closeButtonRef.current;
    if (!target) return;
    target.focus();
  }, [index, isOpen, showPlayOverlay]);

  if (!isOpen || !media) return null;

  return (
    <ModalBackdrop onClick={onClose}>
      <ModalContent onClick={(event) => event.stopPropagation()}>
        <ModalHeader>
          <ModalHeaderControls>
            <ModalNavButton
              type="button"
              onClick={() => setIndex((prev) => (prev - 1 + total) % total)}
              aria-label="Previous"
            >
              &#171;
            </ModalNavButton>
            <ModalNavButton
              type="button"
              onClick={() => setIndex((prev) => (prev + 1) % total)}
              aria-label="Next"
            >
              &#187;
            </ModalNavButton>
            <ModalToggleButton
              type="button"
              $active={autoplay}
              onClick={() => setAutoplay((prev) => !prev)}
            >
              Autoplay
            </ModalToggleButton>
          </ModalHeaderControls>
          <ModalClose ref={closeButtonRef} type="button" onClick={onClose} aria-label="Close">
            &#10005;
          </ModalClose>
        </ModalHeader>
        <ModalBody>
          {media.videoPath ? (
            <>
              <ModalVideo
                ref={videoRef}
                poster={media.videoPosterPath || media.imagePath || undefined}
                controls
                onPlay={() => {
                  setIsPlaying(true);
                  setVideoAutoplayEnabled(true);
                }}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
                onError={() => {
                  if (
                    media.isMux
                    && media.muxMp4FallbackPath
                    && activeVideoSrc !== media.muxMp4FallbackPath
                  ) {
                    setActiveVideoSrc(media.muxMp4FallbackPath);
                  }
                }}
              />
              {showPlayOverlay ? (
                <ModalPlayButton
                  ref={overlayPlayButtonRef}
                  type="button"
                  onClick={() => {
                    const video = videoRef.current;
                    if (!video) return;
                    setVideoAutoplayEnabled(true);
                    setIsPlaying(true);
                    const playPromise = video.play();
                    if (playPromise && typeof playPromise.catch === 'function') {
                      playPromise.catch(() => {});
                    }
                  }}
                  aria-label="Play"
                >
                  ▶
                </ModalPlayButton>
              ) : null}
            </>
          ) : (
            <ModalVideo as="img" src={media.imagePath || media.videoPosterPath} alt={media.title} />
          )}
        </ModalBody>
        <ModalTitle>{media.title}</ModalTitle>
        {media.description ? (
          <ModalDescription>{media.description}</ModalDescription>
        ) : null}
        {autoplay && !media.videoPath ? (
          <ModalProgress>
            <ModalProgressBar $progress={imageProgress} />
            <ModalProgressLabel>
              Next in {Math.max(0, Math.ceil((1 - imageProgress) * 5))}s
            </ModalProgressLabel>
          </ModalProgress>
        ) : null}
      </ModalContent>
    </ModalBackdrop>
  );
}

export default PlayerModal;

PlayerModal.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  items: arrayOf(shape),
  initialIndex: number,
}.isRequired;
